/**
 * QrCode - renders arbitrary text as a scannable QR code.
 *
 * The matrix is encoded locally (`uqr`) and drawn as a single SVG `<path>`
 * rather than one `<rect>` per module: a version-5 code is ~1,400 dark modules,
 * and 1,400 DOM nodes is a visible cost for a static image.
 *
 * `uqr` also ships `renderSVG`, which returns markup — using it would mean
 * `dangerouslySetInnerHTML`. Building the path here keeps the render tree
 * ordinary React and lets the colors follow the app theme.
 */
import { useMemo } from "react";
import { encode } from "uqr";

/**
 * Quiet zone the spec requires around the symbol; `uqr` bakes it into the
 * returned matrix, so the path needs no offset of its own. Its default is 1,
 * which scanners read less reliably than the specified 4.
 */
const QUIET_ZONE = 4;

const buildPath = (matrix: ReadonlyArray<ReadonlyArray<boolean>>): string => {
  const parts: Array<string> = [];
  for (let y = 0; y < matrix.length; y += 1) {
    const row = matrix[y]!;
    let runStart: number | null = null;
    // Horizontal runs collapse into one rectangle each, which cuts the path
    // data roughly in half for typical codes.
    for (let x = 0; x <= row.length; x += 1) {
      const dark = x < row.length && row[x] === true;
      if (dark && runStart === null) {
        runStart = x;
      } else if (!dark && runStart !== null) {
        const width = x - runStart;
        parts.push(`M${runStart} ${y}h${width}v1h-${width}z`);
        runStart = null;
      }
    }
  }
  return parts.join("");
};

export function QrCode({
  value,
  className,
  title,
}: {
  readonly value: string;
  readonly className?: string;
  readonly title: string;
}) {
  const symbol = useMemo(() => {
    const { data, size } = encode(value, { ecc: "M", border: QUIET_ZONE });
    return { path: buildPath(data), extent: size };
  }, [value]);

  return (
    <svg
      viewBox={`0 0 ${symbol.extent} ${symbol.extent}`}
      className={className}
      role="img"
      aria-label={title}
      shapeRendering="crispEdges"
    >
      {/* White background is not cosmetic: scanners need the light/dark
          contrast to be the way round the spec expects, so this stays fixed
          regardless of the app theme. */}
      <rect width={symbol.extent} height={symbol.extent} fill="#ffffff" />
      <path d={symbol.path} fill="#000000" />
    </svg>
  );
}
